import React, { useEffect, useState } from "react";
import { goToCheckout } from "@/lib/checkout";
import { api as _api, API, getB2CToken } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PublicationPlanPicker from "@/components/PublicationPlanPicker";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import Logo from "@/components/Logo";
import { isAfrican } from "@/lib/countries";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { FindoSpinner, WaitingNotice } from "@/components/FindoSpinner";
import { CheckCircle2, XCircle, User, Mail, Phone, CreditCard, RefreshCw, Download, ShieldCheck, Check } from "lucide-react";

const ROWS = [
  ["fa.fDate", "lost_date", "found_date"], ["fa.fPlace", "lost_place", "found_place"], ["fa.fCity", "city", "city"],
  ["fa.fCircumstances", "circumstances", "circumstances"], ["fa.fColor", "color", "color"],
  ["fa.fCondition", "condition", "condition"], ["fa.fBrand", "brand", "brand"], ["fa.fQuantity", "quantity", "quantity"],
];

export const ContactCard = ({ title, c, testid }) => {
  const { t } = useLang();
  return (
  <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3.5 text-sm" data-testid={testid}>
    <p className="text-xs font-semibold text-emerald-800">{title}</p>
    <p className="mt-1.5 font-semibold text-slate-800 flex items-center gap-1.5"><User size={13} /> {c.name}</p>
    <p className="text-slate-600 flex items-center gap-1.5 mt-1"><Mail size={13} /> <a href={`mailto:${c.email}`} className="underline hover:text-findo-navy">{c.email}</a></p>
    <p className="text-slate-600 flex items-center gap-1.5 mt-0.5" data-testid={`${testid}-phone`}><Phone size={13} />
      {c.phone ? <a href={`tel:${c.phone}`} className="underline hover:text-findo-navy">{c.phone}</a> : <span className="italic text-slate-400">{t("fa.phoneMissing")}</span>}
    </p>
  </div>
  );
};

const OWNER_SAFETY_TIPS = ["fa.ownerSafety1", "fa.ownerSafety2", "fa.ownerSafety3"];

function OwnerSafetyTips({ ad, r, reload, t }) {
  const [busy, setBusy] = useState(false);
  async function ack() {
    setBusy(true);
    try { await api(`/b2c/ads/${ad.id}/responses/${r.id}/safety-ack`, { method: "POST", b2c: true }); reload?.(); }
    catch (e) { toast.error(e.message); setBusy(false); }
  }
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 space-y-3" data-testid={`owner-safety-tips-${r.id}`}>
      <p className="text-sm font-semibold text-amber-900 flex items-center gap-1.5"><ShieldCheck size={16} /> {t("fa.safetyTitle")}</p>
      <ul className="space-y-2">
        {OWNER_SAFETY_TIPS.map((k) => (
          <li key={k} className="flex items-start gap-2 text-sm text-amber-900/90"><Check size={14} className="mt-1 shrink-0" /> <span>{t(k)}</span></li>
        ))}
      </ul>
      <Button onClick={ack} disabled={busy} className="w-full rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90 text-white" data-testid={`owner-safety-ack-${r.id}`}>
        {busy ? <FindoSpinner size={16} /> : t("fa.safetyOk")}
      </Button>
    </div>
  );
}

function Comparison({ mine, theirs, t, found }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border text-xs">
      <div className="grid grid-cols-[1fr_1.4fr_1.4fr] bg-slate-50 font-semibold text-slate-500">
        <span className="p-2" /><span className="p-2">{t(found ? "fa.colYourAd" : "fa.colYours")}</span><span className="p-2 text-findo-red">{t(found ? "fa.colOwner" : "fa.colFinder")}</span>
      </div>
      {ROWS.map(([label, a, b]) => (
        <div key={label} className="grid grid-cols-[1fr_1.4fr_1.4fr] border-t border-border">
          <span className="p-2 font-semibold text-slate-500">{t(label)}</span>
          <span className="p-2 text-slate-700 break-words">{mine?.[a] ?? "—"}</span>
          <span className="p-2 text-slate-900 font-medium break-words">{theirs?.[b] ?? "—"}</span>
        </div>
      ))}
    </div>
  );
}

function PhotoReview({ ad, r, reload, t }) {
  const [busy, setBusy] = useState(false);
  const [confirmNo, setConfirmNo] = useState(false);
  async function decide(action) {
    setConfirmNo(false);
    setBusy(true);
    try {
      await api(`/b2c/ads/${ad.id}/responses/${r.id}/${action}`, { method: "POST", b2c: true });
      toast.success(t(action === "approve" ? "fa.photoLooksLikeDone" : "fa.photoNotMineDone"));
      reload?.();
    } catch (e) { toast.error(e.message); setBusy(false); }
  }
  return (
    <div className="rounded-2xl border border-border p-3.5 space-y-3" data-testid={`response-${r.id}`}>
      <p className="text-sm font-semibold text-findo-navy">{t("fa.photoReviewTitle")}</p>
      {r.photo && <img src={r.photo} alt="" className="max-h-72 w-full object-contain rounded-xl border border-border bg-slate-50" data-testid={`response-photo-${r.id}`} />}
      <div className="flex flex-col sm:flex-row gap-2">
        <Button onClick={() => decide("approve")} disabled={busy} className="flex-1 rounded-full font-semibold bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`response-photo-yes-${r.id}`}>
          {busy ? <FindoSpinner size={16} /> : <><CheckCircle2 size={15} className="mr-1.5" /> {t("fa.photoLooksLike")}</>}
        </Button>
        <Button onClick={() => setConfirmNo(true)} disabled={busy} variant="outline" className="flex-1 rounded-full font-semibold text-slate-600" data-testid={`response-photo-no-${r.id}`}>
          <XCircle size={15} className="mr-1.5" /> {t("fa.photoNotMine")}
        </Button>
      </div>
      <AlertDialog open={confirmNo} onOpenChange={setConfirmNo}>
        <AlertDialogContent data-testid={`response-photo-no-confirm-${r.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("fa.photoNotMine")}</AlertDialogTitle>
            <AlertDialogDescription>{t("fa.photoNotMineConfirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`response-photo-no-cancel-${r.id}`}>{t("c.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => decide("reject")} data-testid={`response-photo-no-ok-${r.id}`}>{t("fa.photoNotMine")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function LostResponseRow({ ad, r, t, reload }) {
  const { lang } = useLang();
  if (r.owner_photo_status === "pending") return <PhotoReview ad={ad} r={r} reload={reload} t={t} />;
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(lang, { day: "2-digit", month: "short", year: "numeric" }) : "—");
  const status = { approved: ["fa.finderConfirmed", "bg-emerald-50 text-emerald-700"], rejected: ["fa.notYourItem", "bg-slate-100 text-slate-500"] }[r.review_status]
    || ["fa.waitFinderDecisionOwner", "bg-amber-50 text-amber-700"];
  return (
    <div className="rounded-2xl border border-border p-3.5 space-y-3" data-testid={`response-${r.id}`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
        <span className="flex items-center gap-1.5 font-semibold text-slate-800" data-testid={`response-first-name-${r.id}`}><User size={14} /> {r.first_name || "—"}</span>
        <span className="text-slate-600 truncate max-w-[16rem]" data-testid={`response-item-${r.id}`}>{ad.description}</span>
        <span className="text-xs text-slate-400" data-testid={`response-date-${r.id}`}>{t("fa.reportedOn")} {fmtDate(r.created_at)}</span>
        <span className={`ml-auto text-xs font-bold px-2.5 py-1 rounded-full ${status[1]}`} data-testid={`response-status-${r.id}`}>{t(status[0])}</span>
      </div>
      {r.review_status === "approved" && !r.owner_safety_ack && <OwnerSafetyTips ad={ad} r={r} reload={reload} t={t} />}
      {r.contact && <ContactCard title={t("fa.finderContact")} c={r.contact} testid={`response-contact-${r.id}`} />}
      {r.finder_closed && <p className="text-xs font-semibold text-slate-500" data-testid={`response-finder-closed-${r.id}`}>{t("fa.finderClosedNote")}</p>}
    </div>
  );
}

function ResponseCard({ ad, r, reload, t }) {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const found = ad.ad_type === "found";
  async function decide(action) {
    setConfirm(null);
    setBusy(true);
    try {
      await api(`/b2c/ads/${ad.id}/responses/${r.id}/${action}`, { method: "POST", b2c: true });
      toast.success(t(action === "approve" ? "fa.approved" : "fa.rejected"));
      reload?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  if (r.review_status === "rejected") {
    return <p className="text-xs text-slate-400 flex items-center gap-1.5" data-testid={`response-rejected-${r.id}`}><XCircle size={13} /> {t("fa.rejected")}</p>;
  }
  return (
    <div className="rounded-2xl border border-border p-3.5 space-y-3" data-testid={`response-${r.id}`}>
      <div className={r.photo_2 ? "grid grid-cols-2 gap-2" : ""}>
        {r.photo && <img src={r.photo} alt="" className="h-36 w-full object-cover rounded-xl border border-border" data-testid={`response-photo-${r.id}`} />}
        {r.photo_2 && <img src={r.photo_2} alt="" className="h-36 w-full object-cover rounded-xl border border-border" data-testid={`response-photo2-${r.id}`} />}
      </div>
      <Comparison mine={ad.owner_declaration} theirs={r.details} t={t} found={found} />
      {r.message && <p className="text-xs text-slate-500 italic">{t("fa.fMessage")} : « {r.message} »</p>}
      {r.review_status === "approved" ? (r.contact
        ? <ContactCard title={t(found ? "fa.ownerContact" : "fa.finderContact")} c={r.contact} testid={`response-contact-${r.id}`} />
        : <WaitingNotice title={t("fa.waitUnlockTitle")} text={t("fa.waitUnlockText")} testid={`response-wait-unlock-${r.id}`} />
      ) : !ad.accepted_contact_id && (
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setConfirm("approve")} disabled={busy} className="flex-1 rounded-full font-semibold bg-emerald-600 hover:bg-emerald-700" data-testid={`response-approve-${r.id}`}>
            <CheckCircle2 size={14} className="mr-1.5" /> {t("fa.approve")}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setConfirm("reject")} disabled={busy} className="flex-1 rounded-full font-semibold text-slate-600" data-testid={`response-reject-${r.id}`}>
            <XCircle size={14} className="mr-1.5" /> {t("fa.reject")}
          </Button>
        </div>
      )}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid={`response-confirm-dialog-${r.id}`}>
          <AlertDialogHeader>
            <div className="flex items-center gap-2.5"><Logo size={30} /><AlertDialogTitle className="font-display text-findo-navy">{t("fa.confirmTitle")}</AlertDialogTitle></div>
            <AlertDialogDescription className="text-slate-600">
              {confirm && t(confirm === "approve" ? (found ? "fa.approveClaimConfirm" : "fa.approveConfirm") : "fa.rejectConfirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid={`response-confirm-cancel-${r.id}`}>{t("fa.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => decide(confirm)} data-testid={`response-confirm-ok-${r.id}`}
              className={`rounded-full font-semibold ${confirm === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-findo-red hover:bg-findo-red/90"}`}>
              {confirm === "approve" ? t("fa.approve") : t("fa.reject")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PendingPayment({ ad, t }) {
  const [busy, setBusy] = useState(false);
  async function pay() {
    setBusy(true);
    try {
      const res = await api(`/b2c/ads/${ad.id}/publication-checkout`, { method: "POST", b2c: true, body: { origin_url: window.location.origin } });
      if (res?.url) goToCheckout(res.url);
    } catch (e) { toast.error(e.message); setBusy(false); }
  }
  return (
    <WaitingNotice title={t("fa.pendingPayTitle")} text={t("fa.pendingPayText")} testid="ad-pending-payment">
      <Button size="sm" onClick={pay} disabled={busy} className="mt-2.5 rounded-full font-semibold bg-findo-red hover:bg-findo-red/90" data-testid="ad-finish-payment-btn">
        <CreditCard size={14} className="mr-1.5" /> {busy ? <FindoSpinner size={16} /> : t("fa.finishPay")}
      </Button>
    </WaitingNotice>
  );
}

function RenewDialog({ ad, open, onClose, t, reload }) {
  const [pub, setPub] = useState({ plan: "30d", boost: null });
  const [busy, setBusy] = useState(false);
  const expired = new Date(ad.expires_at) <= new Date();
  const africa = isAfrican(ad.country);
  async function renew() {
    setBusy(true);
    try {
      const res = await _api(`/b2c/ads/${ad.id}/renew`, { method: "POST", b2c: true, body: { plan: pub.plan, boost_tier: pub.boost, origin_url: window.location.origin } });
      if (res?.url) { goToCheckout(res.url); return; }
      toast.success(t("fa.renewed")); onClose(); reload?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="renew-ad-dialog">
        <DialogHeader><DialogTitle className="font-display text-findo-navy">{t("fa.renewTitle")}</DialogTitle></DialogHeader>
        {!expired && !africa && <p className="rounded-xl bg-findo-red/10 text-findo-red text-sm font-semibold px-3.5 py-2.5" data-testid="renew-discount-banner">{t("fa.renewDiscountBanner")}</p>}
        <PublicationPlanPicker plan={pub.plan} boost={pub.boost} onChange={setPub} country={ad.country} discount={expired || africa ? 0 : 0.5} title={t("fa.renewTitle")} />
        <Button onClick={renew} disabled={busy} className="w-full h-11 rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="renew-submit-btn">
          {busy ? <FindoSpinner size={16} /> : t("fa.renewBtn")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function RenewBar({ ad, t, reload, autoOpen }) {
  const [open, setOpen] = useState(false);
  useEffect(() => { if (autoOpen) setOpen(true); }, [autoOpen]);
  const expired = new Date(ad.expires_at) <= new Date();
  async function receipt() {
    try {
      const res = await fetch(`${API}/b2c/ads/${ad.id}/publication-receipt.pdf`, { headers: { Authorization: `Bearer ${getB2CToken()}` } });
      if (!res.ok) throw new Error(t("fa.noReceipt"));
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a"); a.href = url; a.download = `recu-publication-${ad.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <p className={`text-xs font-semibold mr-auto ${expired ? "text-findo-danger" : "text-slate-400"}`} data-testid="ad-expires-at">
        {expired ? t("fa.expired") : `${t("fa.expiresOn")} ${new Date(ad.expires_at).toLocaleDateString()}`}
      </p>
      {!ad.publication_free && (
        <Button size="sm" variant="outline" onClick={receipt} className="rounded-full font-semibold" data-testid="ad-publication-receipt-btn">
          <Download size={14} className="mr-1.5" /> {t("fa.receipt")}
        </Button>
      )}
      <Button size="sm" onClick={() => setOpen(true)} className="rounded-full font-semibold bg-findo-red hover:bg-findo-red/90" data-testid="ad-renew-btn">
        <RefreshCw size={14} className="mr-1.5" /> {expired || isAfrican(ad.country) ? t("fa.renewBtn") : t("fa.renewEarly")}
      </Button>
      <RenewDialog ad={ad} open={open} onClose={() => setOpen(false)} t={t} reload={reload} />
    </div>
  );
}

export default function LostResponsesReview({ ad, reload, autoRenew }) {
  const { t } = useLang();
  const found = ad.ad_type === "found";
  const closed = ["resolved", "closed", "restituted"].includes(ad.status);
  if (ad.status === "pending_payment") return <div className="mt-4"><PendingPayment ad={ad} t={t} /></div>;
  const responses = ad.responses || [];
  const active = responses.filter((r) => r.review_status !== "rejected");
  return (
    <div className="mt-4 border-t border-border pt-4 space-y-3" data-testid="lost-responses-review">
      {!found && ad.expires_at && !closed && <RenewBar ad={ad} t={t} reload={reload} autoOpen={autoRenew} />}
      {!closed && active.length === 0 && (
        <WaitingNotice title={t("fa.waitFinderTitle")} text={t(found ? "fa.waitClaimantText" : "fa.waitFinderText")} testid="ad-waiting-finder" />
      )}
      {responses.length > 0 && (
        <>
          <div>
            <p className="text-sm font-semibold text-findo-navy">{t(found ? "fa.claimsTitle" : "fa.responsesTitle")}</p>
            <p className="text-xs text-slate-500">{found ? t("fa.compareClaimHint") : responses.some((r) => r.owner_photo_status === "pending") ? t("fa.ownerPhotoHint") : responses.some((r) => r.review_status === "pending") ? t("fa.ownerWaitHint") : null}</p>
          </div>
          {responses.map((r) => found
            ? <ResponseCard key={r.id} ad={ad} r={r} reload={reload} t={t} />
            : <LostResponseRow key={r.id} ad={ad} r={r} t={t} reload={reload} />)}
        </>
      )}
    </div>
  );
}
